// Script execution block — terminal-style display of script runs.
// Copied from frontend/src/components/agent/ScriptExecutionBlock.tsx.
// Generic — no Platinum-specific dependencies.
// See ../../docs/90-provenance-map.md.

import React, { useCallback, useState } from 'react'
import { Box, Typography, Chip, IconButton, Tooltip, useTheme } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { toolStatusPresentation } from './ToolCallGroup.js'
import { CARD_RADIUS, HAIRLINE_COLOR, INNER_RADIUS } from './surface.js'

// A block cap, not a "read everything" limit -- long tool output gets a
// scrollbar of its own instead of pushing the whole chat down, with "Show
// all" for when you actually want the rest.
const DEFAULT_VISIBLE_LINES = 9
const COLLAPSED_MAX_HEIGHT = 190

interface ScriptExecutionBlockProps {
  scriptFile: string
  language: string
  fullCommand: string
  output?: string
  status: 'running' | 'complete' | 'error' | 'cancelled'
  elapsedSeconds?: number
  /** Inside another card (a tool chain): take the inner corner, not the card's. */
  nested?: boolean
}

interface ParsedCommandOutput {
  stdout: string
  stderr: string
}

/**
 * The shape a Bash tool call's raw output takes: a stdout/stderr envelope
 * plus a few housekeeping flags (interrupted, isImage, noOutputExpected)
 * that say nothing about what the command produced. Recognised only when
 * `stdout` is a string on the parsed object -- anything else (plain text,
 * some other tool's JSON) falls back to showing the raw text untouched.
 */
function parseCommandOutput(output: string): ParsedCommandOutput | null {
  let parsed: unknown
  try {
    parsed = JSON.parse(output)
  } catch {
    return null
  }
  if (
    parsed && typeof parsed === 'object' && !Array.isArray(parsed)
    && typeof (parsed as Record<string, unknown>).stdout === 'string'
  ) {
    const obj = parsed as Record<string, unknown>
    return { stdout: obj.stdout as string, stderr: typeof obj.stderr === 'string' ? obj.stderr : '' }
  }
  return null
}

export default function ScriptExecutionBlock({
  scriptFile,
  language,
  fullCommand,
  output,
  status,
  elapsedSeconds,
  nested = false,
}: ScriptExecutionBlockProps) {
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)
  const theme = useTheme()

  const envelope = output ? parseCommandOutput(output) : null
  const stdoutText = envelope ? envelope.stdout : output
  const stderrText = envelope ? envelope.stderr : ''

  const outputLines = stdoutText ? stdoutText.split('\n') : []
  const totalLines = outputLines.length
  const needsCollapse = totalLines > DEFAULT_VISIBLE_LINES
  const displayOutput = expanded || !needsCollapse
    ? stdoutText
    : outputLines.slice(0, DEFAULT_VISIBLE_LINES).join('\n')

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(stdoutText || '').then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [stdoutText])

  const stopped = status === 'cancelled'
  const presentation = toolStatusPresentation(status)
  const statusIcon = status === 'running' ? '⏳' : presentation.icon
  const statusColor = presentation.color

  const isDark = theme.palette.mode === 'dark'
  const borderColor = isDark ? theme.palette.grey[800] : HAIRLINE_COLOR
  const headerBg = isDark ? theme.palette.grey[900] : '#f1f5f9'
  const headerBorder = isDark ? theme.palette.grey[800] : '#e5e7eb'
  // A softer dark slate rather than near-black, so the block reads as a
  // terminal, not a hole in the page.
  const outputBg = isDark ? '#1e293b' : '#f8fafc'
  const outputText = isDark ? '#cbd5e1' : '#1e293b'
  const commandColor = isDark ? '#7ec699' : '#15803d'
  const stderrColor = isDark ? '#f87171' : '#b91c1c'
  const toggleBg = isDark ? theme.palette.grey[900] : outputBg
  const toggleBorder = isDark ? theme.palette.grey[800] : '#e2e8f0'

  return (
    <Box
      data-testid="script-execution-block"
      sx={{
        border: `1px solid ${borderColor}`,
        borderRadius: nested ? INNER_RADIUS : CARD_RADIUS,
        overflow: 'hidden',
        my: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          backgroundColor: headerBg,
          borderBottom: `1px solid ${headerBorder}`,
        }}
      >
        <Typography component="span" sx={{ fontSize: 14, color: statusColor }}>
          {statusIcon}
        </Typography>
        <Typography
          component="span"
          sx={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {scriptFile}
        </Typography>
        <Chip
          label={language}
          size="small"
          sx={{
            height: 20,
            borderRadius: INNER_RADIUS,
            fontSize: 11,
            fontWeight: 500,
            backgroundColor: isDark ? theme.palette.grey[800] : '#e2e8f0',
            color: isDark ? theme.palette.grey[300] : '#475569',
          }}
        />
        {elapsedSeconds !== undefined && (
          <Typography sx={{ fontSize: 11, color: '#94a3b8', ml: 'auto' }}>
            {elapsedSeconds.toFixed(1)}s
          </Typography>
        )}
        {stdoutText && (
          <Tooltip title={copied ? 'Copied' : 'Copy output'}>
            <IconButton
              size="small"
              onClick={handleCopy}
              aria-label="copy output"
              sx={{ color: copied ? '#22c55e' : '#6b7280', ml: elapsedSeconds !== undefined ? 0.5 : 'auto' }}
            >
              {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          backgroundColor: outputBg,
          color: outputText,
          fontFamily: '"Fira Code", "Consolas", monospace',
          fontSize: 12,
          lineHeight: 1.5,
          p: '8px 12px',
          overflow: 'auto',
          maxHeight: expanded ? 'none' : COLLAPSED_MAX_HEIGHT,
        }}
      >
        <Box component="pre" sx={{ m: 0, color: commandColor, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          $ {fullCommand}
        </Box>

        {displayOutput && (
          <Box
            component="pre"
            sx={{
              m: 0,
              mt: 0.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              color: outputText,
            }}
          >
            {displayOutput}
          </Box>
        )}

        {stderrText && (
          <Box
            component="pre"
            sx={{
              m: 0,
              mt: 0.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              color: stderrColor,
            }}
          >
            {stderrText}
          </Box>
        )}

        {status === 'running' && !output && (
          <Typography sx={{ color: commandColor, fontSize: 12, mt: 0.5, fontFamily: 'inherit' }}>
            Running...
          </Typography>
        )}

        {stopped && (
          <Typography sx={{ color: '#9ca3af', fontSize: 12, mt: 0.5, fontFamily: 'inherit' }}>
            cancelled
          </Typography>
        )}
      </Box>

      {needsCollapse && (
        <Box
          onClick={() => setExpanded(prev => !prev)}
          sx={{
            px: 1.5,
            py: 0.5,
            backgroundColor: toggleBg,
            borderTop: `1px solid ${toggleBorder}`,
            textAlign: 'center',
            cursor: 'pointer',
            '&:hover': { backgroundColor: isDark ? theme.palette.grey[800] : '#eef2f7' },
          }}
        >
          <Typography sx={{ fontSize: 12, color: '#2563eb', fontWeight: 500 }}>
            {expanded ? 'Show less' : `Show all ${totalLines} lines`}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
